import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button, DocumentQRCode, PrintStyles, PoweredByDuoNep } from '@components/common'
import { useDocumentRegistry } from '@hooks/useDocumentRegistry'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'
import { formatDate, formatDateTime } from '@utils/dateUtils'

// Fiscal year months (June to May)
const FISCAL_MONTHS = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May']

// Get week of month (1-4) from a date
const getWeekOfMonth = (date) => {
  const d = new Date(date)
  const day = d.getDate()
  return Math.ceil(day / 7)
}

// Get fiscal month index (0-11 where 0=June)
const getFiscalMonthIndex = (date) => {
  const d = new Date(date)
  const month = d.getMonth() // 0-11 (Jan=0, Dec=11)
  // Convert to fiscal year (June=0, May=11)
  // Jan(0) -> 7, Feb(1) -> 8, Mar(2) -> 9, Apr(3) -> 10, May(4) -> 11
  // Jun(5) -> 0, Jul(6) -> 1, Aug(7) -> 2, Sep(8) -> 3, Oct(9) -> 4, Nov(10) -> 5, Dec(11) -> 6
  return (month + 7) % 12
}

const PMGanttPrint = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const { orgName, orgLogoUrl, systemSubtitle } = useBranding()
  const [loading, setLoading] = useState(true)
  const [schedules, setSchedules] = useState([])
  const [departments, setDepartments] = useState([])
  const [facilities, setFacilities] = useState([])
  const { registerDocument, verificationCode, loading: registering } = useDocumentRegistry()

  // Filter states - default to current fiscal year
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  // Fiscal year starts in June
  const defaultFiscalYear = currentMonth >= 5 ? currentYear : currentYear - 1

  const [fiscalYear, setFiscalYear] = useState(
    searchParams.get('fiscalYear') || defaultFiscalYear.toString()
  )
  const [facilityFilter, setFacilityFilter] = useState(searchParams.get('facility') || '')
  const [departmentFilter, setDepartmentFilter] = useState(searchParams.get('department') || '')

  // Calculate date range from fiscal year
  const startDate = `${fiscalYear}-06-01`
  const endDate = `${parseInt(fiscalYear) + 1}-05-31`

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const params = {
          start_date: startDate,
          end_date: endDate,
          page_size: 1000, // Get all schedules for the year
        }
        if (facilityFilter) params.facility = facilityFilter
        if (departmentFilter) params.department = departmentFilter

        const [pmRes, deptRes] = await Promise.all([
          api.get('/maintenance/pm-schedules/', { params }),
          api.get('/common/departments/', { params: { page_size: 100 } }),
        ])

        setSchedules(pmRes.data.results || pmRes.data || [])
        setDepartments(deptRes.data.results || deptRes.data || [])
      } catch (error) {
        console.error('Error fetching PM schedules:', error)
        toast.error('Failed to load PM schedules')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [startDate, endDate, facilityFilter, departmentFilter])

  // Set document title for meaningful PDF filename
  useEffect(() => {
    document.title = `PM Plan FY${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(-2)} - DuoNep`
    return () => {
      document.title = 'DuoNep'
    }
  }, [fiscalYear])

  // Group schedules by asset and calculate PM schedule for each month/week
  const equipmentRows = useMemo(() => {
    const assetMap = new Map()

    schedules.forEach(schedule => {
      const assetId = schedule.asset
      if (!assetMap.has(assetId)) {
        assetMap.set(assetId, {
          id: assetId,
          name: schedule.asset_name,
          department: schedule.department_name || 'Unknown',
          unitCount: schedule.unit_count || 1,
          frequencyPerYear: schedule.frequency_per_year || 4,
          maintenanceProvider: schedule.maintenance_provider_display || 'In-house BME',
          facility: schedule.facility_name || 'UCI Main',
          // 12 months x 4 weeks = 48 cells
          weeklySchedule: Array(12).fill(null).map(() => Array(4).fill(null)),
        })
      }

      const asset = assetMap.get(assetId)
      const scheduledDate = new Date(schedule.scheduled_date)

      // Check if the scheduled date is within our fiscal year range
      const scheduleStart = new Date(startDate)
      const scheduleEnd = new Date(endDate)

      if (scheduledDate >= scheduleStart && scheduledDate <= scheduleEnd) {
        const monthIndex = getFiscalMonthIndex(scheduledDate)
        const weekIndex = Math.min(getWeekOfMonth(scheduledDate) - 1, 3) // 0-3

        asset.weeklySchedule[monthIndex][weekIndex] = {
          id: schedule.id,
          status: schedule.status,
          date: schedule.scheduled_date,
        }
      }
    })

    // Sort by department, then by name
    return Array.from(assetMap.values()).sort((a, b) => {
      const deptCompare = a.department.localeCompare(b.department)
      if (deptCompare !== 0) return deptCompare
      return a.name.localeCompare(b.name)
    })
  }, [schedules, startDate, endDate])

  // Summary stats
  const stats = useMemo(() => {
    const total = schedules.length
    const scheduled = schedules.filter(s => s.status === 'scheduled').length
    const completed = schedules.filter(s => s.status === 'completed').length
    const overdue = schedules.filter(s => s.status === 'overdue').length
    const uniqueAssets = new Set(schedules.map(s => s.asset)).size
    return { total, scheduled, completed, overdue, uniqueAssets }
  }, [schedules])

  // Generate document number with timestamp for unique identification
  const generateDocumentNumber = () => {
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
    const timeStr = now.toTimeString().slice(0, 5).replace(':', '')
    return `PM Plan FY${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(-2)} - ${dateStr}-${timeStr} (${equipmentRows.length} equipment)`
  }

  // Register document when schedules are loaded (after useMemo definitions)
  useEffect(() => {
    if (schedules.length > 0 && !verificationCode && !loading && equipmentRows.length > 0) {
      const documentNumber = generateDocumentNumber()
      const documentData = {
        title: `PM Plan FY${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(-2)}`,
        document_number: documentNumber,
        fiscal_year: fiscalYear,
        filters: { facility: facilityFilter, department: departmentFilter },
        equipment_count: equipmentRows.length,
      }
      registerDocument('pm_schedule', documentNumber, null, documentData, {
        fiscal_year: fiscalYear,
        start_date: startDate,
        end_date: endDate,
        record_count: schedules.length,
      }).catch((err) => console.error('Failed to register document:', err))
    }
  }, [schedules, verificationCode, loading, fiscalYear, facilityFilter, departmentFilter, equipmentRows, startDate, endDate, registerDocument])

  const handlePrint = async () => {
    if (!verificationCode && schedules.length > 0) {
      try {
        const documentNumber = generateDocumentNumber()
        const documentData = {
          title: `PM Plan FY${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(-2)}`,
          document_number: documentNumber,
          fiscal_year: fiscalYear,
          filters: { facility: facilityFilter, department: departmentFilter },
          summary: stats,
          equipment_count: equipmentRows.length,
        }
        await registerDocument('pm_schedule', documentNumber, null, documentData, {
          fiscal_year: fiscalYear,
          start_date: startDate,
          end_date: endDate,
          record_count: schedules.length,
        })
      } catch (err) {
        console.error('Registration failed:', err)
      }
    }
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box className="print-controls no-print" sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate('/pm-schedule')}
        >
          Back
        </Button>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Fiscal Year</InputLabel>
          <Select
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            label="Fiscal Year"
          >
            {[...Array(5)].map((_, i) => {
              const year = currentYear - 2 + i
              return (
                <MenuItem key={year} value={year.toString()}>
                  FY {year}-{(year + 1).toString().slice(-2)}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Facility</InputLabel>
          <Select
            value={facilityFilter}
            onChange={(e) => setFacilityFilter(e.target.value)}
            label="Facility"
          >
            <MenuItem value="">All Facilities</MenuItem>
            <MenuItem value="uci_main">UCI Main</MenuItem>
            <MenuItem value="gulu">Gulu Cancer Center</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            label="Department"
          >
            <MenuItem value="">All Departments</MenuItem>
            {departments.map(dept => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={registering}
        >
          {registering ? 'Preparing...' : 'Print / Save as PDF'}
        </Button>
      </Box>

      {/* Printable Content */}
      <Box className="print-content" sx={{ bgcolor: 'white', p: 1 }}>
        {/* Co-branded header */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          {/* Organization logo centered on first line */}
          {orgLogoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <img
                src={orgLogoUrl}
                alt={orgName}
                style={{ height: '60px' }}
              />
            </Box>
          )}
          {/* Organization name */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#212B36' }}>
            {orgName}
          </Typography>
          {/* System subtitle */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              color: '#1B5E7A',
              letterSpacing: '0.5px',
              mb: 1
            }}
          >
            {systemSubtitle}
          </Typography>
          {/* Section name */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 0.5 }}
          >
            BIOMEDICAL ENGINEERING AND MAINTENANCE SECTION
          </Typography>
          {/* Document title */}
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', textDecoration: 'underline', mb: 0.5 }}
          >
            MEDICAL EQUIPMENT PREVENTIVE MAINTENANCE PLAN
          </Typography>
          <Typography variant="body2">
            Fiscal Year: {fiscalYear}/{parseInt(fiscalYear) + 1}
          </Typography>
        </Box>

        {/* Main Table - Excel-like Gantt */}
        <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'visible' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1400 }}>
            <TableHead>
              {/* Month Headers Row */}
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    width: 200,
                    minWidth: 200,
                    borderRight: '1px solid #ccc',
                    position: 'sticky',
                    left: 0,
                    bgcolor: '#f5f5f5',
                    zIndex: 2,
                  }}
                  rowSpan={2}
                >
                  Medical Equipment
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                    borderRight: '1px solid #ccc',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                  rowSpan={2}
                >
                  Departments/Wards
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    width: 50,
                    textAlign: 'center',
                    borderRight: '1px solid #ccc',
                  }}
                  rowSpan={2}
                >
                  # of units
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    width: 50,
                    textAlign: 'center',
                    borderRight: '1px solid #ccc',
                  }}
                  rowSpan={2}
                >
                  # of times
                </TableCell>
                {/* Month Headers */}
                {FISCAL_MONTHS.map((month, idx) => (
                  <TableCell
                    key={month}
                    colSpan={4}
                    sx={{
                      fontWeight: 'bold',
                      textAlign: 'center',
                      borderRight: '1px solid #ccc',
                      borderBottom: 'none',
                      p: 0.5,
                      fontSize: '0.75rem',
                    }}
                  >
                    {month}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                    width: 100,
                    minWidth: 100,
                    borderLeft: '1px solid #ccc',
                  }}
                  rowSpan={2}
                >
                  Action by
                </TableCell>
              </TableRow>
              {/* Week Numbers Row */}
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {FISCAL_MONTHS.map((month) => (
                  [1, 2, 3, 4].map((week) => (
                    <TableCell
                      key={`${month}-${week}`}
                      sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: 20,
                        minWidth: 20,
                        maxWidth: 20,
                        p: 0.25,
                        fontSize: '0.65rem',
                        borderRight: week === 4 ? '1px solid #ccc' : '1px solid #eee',
                      }}
                    >
                      {week}
                    </TableCell>
                  ))
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {equipmentRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={56} sx={{ textAlign: 'center', py: 4 }}>
                    No PM schedules found for the selected period.
                  </TableCell>
                </TableRow>
              ) : (
                equipmentRows.map((row, rowIdx) => (
                  <TableRow key={row.id || rowIdx} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                    <TableCell
                      sx={{
                        fontSize: '0.75rem',
                        borderRight: '1px solid #ccc',
                        position: 'sticky',
                        left: 0,
                        bgcolor: 'white',
                        zIndex: 1,
                      }}
                    >
                      {row.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.75rem',
                        borderRight: '1px solid #ccc',
                        maxWidth: 120,
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >
                      {row.department}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem', borderRight: '1px solid #ccc' }}>
                      {row.unitCount}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem', borderRight: '1px solid #ccc' }}>
                      {row.frequencyPerYear}
                    </TableCell>
                    {/* Weekly Schedule Cells */}
                    {row.weeklySchedule.map((monthWeeks, monthIdx) => (
                      monthWeeks.map((weekData, weekIdx) => (
                        <TableCell
                          key={`${monthIdx}-${weekIdx}`}
                          sx={{
                            p: 0,
                            width: 20,
                            minWidth: 20,
                            maxWidth: 20,
                            height: 24,
                            bgcolor: weekData
                              ? weekData.status === 'completed'
                                ? '#10B981'
                                : weekData.status === 'overdue'
                                ? '#EF4444'
                                : '#3B82F6'
                              : 'transparent',
                            borderRight: weekIdx === 3 ? '1px solid #ccc' : '1px solid #eee',
                          }}
                          title={weekData ? `${formatDate(weekData.date)} - ${weekData.status}` : ''}
                        />
                      ))
                    ))}
                    <TableCell sx={{ fontSize: '0.7rem', borderLeft: '1px solid #ccc' }}>
                      {row.maintenanceProvider}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#3B82F6', borderRadius: 0.5 }} />
            <Typography variant="caption">Scheduled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#10B981', borderRadius: 0.5 }} />
            <Typography variant="caption">Completed</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#EF4444', borderRadius: 0.5 }} />
            <Typography variant="caption">Overdue</Typography>
          </Box>
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2">
            <strong>Total Equipment:</strong> {equipmentRows.length} |
            <strong> Total PMs:</strong> {stats.total} |
            <strong> Scheduled:</strong> {stats.scheduled} |
            <strong> Completed:</strong> {stats.completed} |
            <strong> Overdue:</strong> {stats.overdue}
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Printed: {formatDateTime(new Date())}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed by: {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : user?.username || 'N/A'}
            </Typography>
          </Box>
          <DocumentQRCode
            verificationCode={verificationCode}
            documentType="PM Plan"
            documentNumber={`FY${fiscalYear}-${(parseInt(fiscalYear) + 1).toString().slice(-2)}`}
            size={80}
          />
        </Box>

        {/* Powered by DuoNep */}
        <PoweredByDuoNep />
      </Box>

      <PrintStyles orientation="landscape" />
    </>
  )
}

export default PMGanttPrint
